import React, { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { supabase } from '../../../lib/supabase';
import Icon from '../../../components/AppIcon';
import { useNavigate } from 'react-router-dom';

const STATUS_STYLES = {
  scheduled: 'bg-blue-100 dark:bg-blue-900/30 text-blue-600 dark:text-blue-400',
  in_progress: 'bg-amber-100 dark:bg-amber-900/30 text-amber-600 dark:text-amber-400',
  completed: 'bg-green-100 dark:bg-green-900/30 text-green-600 dark:text-green-400',
  cancelled: 'bg-red-100 dark:bg-red-900/30 text-red-500 dark:text-red-400',
};

const STATUS_LABELS = {
  scheduled: 'Scheduled',
  in_progress: 'In Progress',
  completed: 'Completed',
  cancelled: 'Cancelled',
};

const PRIORITY_DOT = {
  urgent: 'bg-red-500',
  high: 'bg-orange-400',
  normal: 'bg-blue-400',
  low: 'bg-gray-400',
};

const RecentJobsFeed = ({ orgId }) => {
  const [jobs, setJobs] = useState([]);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  useEffect(() => {
    if (!orgId) return;
    const fetch = async () => {
      setLoading(true);
      try {
        const { data } = await supabase?.from('jobs')?.select('id, title, status, total_cost, value, priority, scheduled_date, customer_name, service_type, contacts(first_name, last_name)')?.eq('org_id', orgId)?.is('deleted_at', null)?.order('created_at', { ascending: false })?.limit(6);
        setJobs(data || []);
      } catch (err) {
        console.error('Recent jobs error:', err);
      } finally {
        setLoading(false);
      }
    };
    fetch();
  }, [orgId]);

  const getClientName = (job) => {
    if (job?.customer_name) return job?.customer_name;
    if (job?.contacts) return `${job?.contacts?.first_name} ${job?.contacts?.last_name}`?.trim();
    return 'No client';
  };

  const formatDate = (d) => {
    if (!d) return '';
    return new Date(d)?.toLocaleDateString('en-US', { month: 'short', day: 'numeric' });
  };

  return (
    <motion.div
      className="bg-card border border-border rounded-2xl p-5 shadow-sm"
      initial={{ opacity: 0, y: 16 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.4, delay: 0.1 }}
    >
      <div className="flex items-center justify-between mb-4">
        <div>
          <h3 className="text-base font-semibold text-foreground">Recent Jobs</h3>
          <p className="text-xs text-muted-foreground mt-0.5">Latest activity</p>
        </div>
        <button onClick={() => navigate('/jobs')} className="text-xs text-primary hover:underline font-medium">View all →</button>
      </div>
      {loading ? (
        <div className="space-y-3">
          {[1,2,3,4,5]?.map(i => <div key={i} className="h-14 bg-muted animate-pulse rounded-xl" />)}
        </div>
      ) : jobs?.length === 0 ? (
        <div className="text-center py-8">
          <Icon name="Briefcase" size={28} className="text-muted-foreground mx-auto mb-2" />
          <p className="text-sm text-muted-foreground">No jobs yet</p>
          <button onClick={() => navigate('/jobs')} className="text-xs text-primary hover:underline mt-1">Create first job</button>
        </div>
      ) : (
        <div className="space-y-2">
          {jobs?.map((job) => (
            <div
              key={job?.id}
              className="flex items-center gap-3 p-3 rounded-xl hover:bg-muted/40 transition-colors cursor-pointer group"
              onClick={() => navigate('/jobs')}
            >
              <div className="flex-shrink-0 relative">
                <div className="w-9 h-9 rounded-xl bg-muted flex items-center justify-center">
                  <Icon name="Wrench" size={15} className="text-muted-foreground" />
                </div>
                {job?.priority && job?.priority !== 'normal' && (
                  <div className={`absolute -top-0.5 -right-0.5 w-2 h-2 rounded-full ${PRIORITY_DOT?.[job?.priority] || 'bg-gray-400'}`} />
                )}
              </div>
              <div className="flex-1 min-w-0">
                <p className="text-sm font-medium text-foreground truncate group-hover:text-primary transition-colors">{job?.title}</p>
                <div className="flex items-center gap-2 mt-0.5">
                  <span className="text-xs text-muted-foreground truncate">{getClientName(job)}</span>
                  {job?.scheduled_date && (
                    <>
                      <span className="text-muted-foreground/40">·</span>
                      <span className="text-xs text-muted-foreground">{formatDate(job?.scheduled_date)}</span>
                    </>
                  )}
                </div>
              </div>
              <div className="flex flex-col items-end gap-1 flex-shrink-0">
                <span className={`text-xs px-2 py-0.5 rounded-full font-medium ${STATUS_STYLES?.[job?.status] || 'bg-muted text-muted-foreground'}`}>
                  {STATUS_LABELS?.[job?.status] || job?.status}
                </span>
                {(Number(job?.total_cost) > 0 || Number(job?.value) > 0) && (
                  <span className="text-xs font-semibold text-foreground">
                    ${(Number(job?.total_cost) || Number(job?.value) || 0)?.toLocaleString()}
                  </span>
                )}
              </div>
            </div>
          ))}
        </div>
      )}
    </motion.div>
  );
};

export default RecentJobsFeed;
